import { useState } from 'react';
import { isSqlNull, renderableText } from '@/lib/values';

/**
 * Expandable tree for STRUCT / LIST / MAP / JSON values.
 *
 * Leaf rendering goes through `renderableText`, so a BIGINT nested three levels
 * deep still cannot reach JSX unconverted.
 */

export interface JsonTreeProps {
  readonly value: unknown;
  /** Dotted/bracketed path to this node, used by "copy key path". */
  readonly path: string;
  /** Indentation steps. Advances by two per level: key label, then value. */
  readonly depth?: number;
  /**
   * Nesting level, tracked separately from `depth` because indentation advances
   * twice per level. Conflating the two collapsed every nested branch on open.
   */
  readonly level?: number;
  readonly onCopyPath: (path: string) => void;
  readonly onCopyValue: (text: string) => void;
}

/** Branches at or below this nesting level start expanded. */
const DEFAULT_OPEN_LEVELS = 2;

function isBranch(value: unknown): value is Record<string, unknown> | unknown[] {
  return typeof value === 'object' && value !== null && !(value instanceof Date) && !ArrayBuffer.isView(value);
}

function leafClass(value: unknown): string {
  if (isSqlNull(value)) return 'v-null';
  if (typeof value === 'string') return value === '' ? 'v-empty' : 'text-[var(--s-string)]';
  if (typeof value === 'number' || typeof value === 'bigint') return 'text-[var(--s-number)]';
  if (typeof value === 'boolean') return 'text-[var(--s-keyword)]';
  return 'text-ink';
}

function leafText(value: unknown): string {
  if (isSqlNull(value)) return 'NULL';
  if (value === '') return '""';
  if (typeof value === 'string') return `"${value}"`;
  return renderableText(value);
}

export function JsonTree({
  value,
  path,
  depth = 0,
  level = 0,
  onCopyPath,
  onCopyValue,
}: JsonTreeProps): JSX.Element {
  const [open, setOpen] = useState(level < DEFAULT_OPEN_LEVELS);

  if (!isBranch(value)) {
    return (
      <div className="group flex items-center gap-1.5 py-0.5" style={{ paddingLeft: depth * 12 }}>
        <span className={`font-mono text-xs ${leafClass(value)}`}>{leafText(value)}</span>
        <CopyActions path={path} text={renderableText(value)} onCopyPath={onCopyPath} onCopyValue={onCopyValue} />
      </div>
    );
  }

  const entries: [string, unknown][] = Array.isArray(value)
    ? value.map((item, index) => [String(index), item])
    : Object.entries(value);

  const open_ = Array.isArray(value) ? '[' : '{';
  const close = Array.isArray(value) ? ']' : '}';

  return (
    <div>
      <div className="group flex items-center gap-1.5 py-0.5" style={{ paddingLeft: depth * 12 }}>
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${path || 'root'}`}
          className="w-3 shrink-0 font-mono text-[10px] text-faint transition-colors hover:text-ink"
          onClick={() => setOpen((current) => !current)}
        >
          {open ? '▾' : '▸'}
        </button>
        <span className="font-mono text-xs text-muted">
          {open_}
          {open ? '' : ` ${entries.length} `}
          {open ? '' : close}
        </span>
        <span className="tnum text-[10px] text-faint">
          {entries.length} {Array.isArray(value) ? 'items' : 'fields'}
        </span>
        <CopyActions
          path={path}
          text={renderableText(value)}
          onCopyPath={onCopyPath}
          onCopyValue={onCopyValue}
        />
      </div>

      {open ? (
        <>
          {entries.map(([key, child]) => {
            const childPath = Array.isArray(value)
              ? `${path}[${key}]`
              : path
                ? `${path}.${key}`
                : key;
            return (
              <div key={childPath}>
                <div
                  className="font-mono text-[11px] text-[var(--s-func)]"
                  style={{ paddingLeft: (depth + 1) * 12 + 18 }}
                >
                  {key}
                </div>
                <JsonTree
                  value={child}
                  path={childPath}
                  depth={depth + 2}
                  level={level + 1}
                  onCopyPath={onCopyPath}
                  onCopyValue={onCopyValue}
                />
              </div>
            );
          })}
          <div className="font-mono text-xs text-muted" style={{ paddingLeft: depth * 12 + 18 }}>
            {close}
          </div>
        </>
      ) : null}
    </div>
  );
}

function CopyActions({
  path,
  text,
  onCopyPath,
  onCopyValue,
}: {
  path: string;
  text: string;
  onCopyPath: (path: string) => void;
  onCopyValue: (text: string) => void;
}): JSX.Element {
  return (
    <span className="ml-auto flex gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
      <button
        type="button"
        className="icon-btn h-[18px] text-[10px]"
        onClick={() => onCopyValue(text)}
        title="Copy value"
      >
        value
      </button>
      {path ? (
        <button
          type="button"
          className="icon-btn h-[18px] text-[10px]"
          onClick={() => onCopyPath(path)}
          title={`Copy key path: ${path}`}
        >
          path
        </button>
      ) : null}
    </span>
  );
}
